<script>
import { mapState } from 'vuex';

import T from './JobagentForm.i18n.js';

import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import BlockInteraction from 'JIX/components/BlockInteraction.vue';
import DsAlert from 'JIX/components/ds/DsAlert.vue';
import DsCheckbox from 'JIX/components/ds/DsCheckbox.vue';
import DsGeoRadiusField from 'JIX/components/ds/DsGeoRadiusField.vue';
import DsRadioButton from 'JIX/components/ds/DsRadioButton.vue';
import PromiseButton from 'JIX/components/PromiseButton.vue';
import RadiusMap from 'JIX/components/map/RadiusMap.vue';
import RenderTranslation from 'JIX/components/RenderTranslation.vue';
import Select2 from 'JIX/components/Select2.vue';
import SentenceList from 'JIX/components/SentenceList.vue';
import SvgIcon from 'JIX/components/SvgIcon.vue';

import { bsTooltip } from 'JIX/vue-bs-tooltip.js';

import ApiConflictError from "JIX/errors/ApiConflictError.js";
import ApiValidationError from "JIX/errors/ApiValidationError.js";

import getGeoAreaConfig from 'JIX/select2/geoarea.js';

import 'icons/tooltip.svg';

export default {
    emits: ['view-jobagent', 'done'],
    components: {
        BlockInteraction,
        DsAlert,
        DsCheckbox,
        DsGeoRadiusField,
        DsRadioButton,
        PromiseButton,
        RadiusMap,
        RenderTranslation,
        Select2,
        SentenceList,
        SvgIcon,
    },
    directives: {
        bsTooltip,
    },
    props: {
        isNew: {
            type: Boolean,
            required: true,
        },
        isLoggedIn: {
            type: Boolean,
            required: true,
        },
        jobagent: {
            type: Object,
            required: false,
            default: null,
        },
        source: {
            type: String,
            required: false,
            default: null,
        },
        explain: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        const formData = {
            enabled: this.jobagent ? this.jobagent.enabled : true,
            search_params: this.jobagent ? cloneDeep(this.jobagent.search_params) : {},
            name: this.jobagent ? this.jobagent.name : null,
            frequency: this.jobagent ? this.jobagent.frequency : 'daily',
            preferred_transportation: this.jobagent ? this.jobagent.preferred_transportation : 'automatic',
        };
        formData.search_params.radius = formData.search_params.radius || 50;
        if (this.showEmailField) {
            formData.email = null;
        }
        const origFormData = cloneDeep(formData);

        const defaultAreas = this.$store.state.geoareaOptions;
        return {
            T,
            wasValidated: false,
            validationErrors: {},
            duplicateJobagent: null,
            processing: false,
            hasChanged: false,
            formData,
            origFormData,
            showAreaField: formData.search_params.geoareaid && formData.search_params.geoareaid.length > 0,
            geoareaConfig: getGeoAreaConfig({
                context: 'job',
                data: [
                    ...defaultAreas,
                    ...(this.jobagent ? this.jobagent.data_names.areas : [])
                        .filter(o => !defaultAreas.find(p => p.id === o.id)),
                ],
                default_list: defaultAreas,
                radius: false,
            }),
            companyConfig: {
                multiple: true,
                minimumInputLength: 2,
                ajax: {
                    url: this.$store.state.urls.findCompany,
                    dataType: 'json',
                    delay: 250,
                    data: (params) => {
                        return {
                            query: params.term,
                            archive: 1
                        };
                    },
                    processResults: (data) => {
                        return { results: data };
                    }
                },
            },
        };
    },
    computed: {
        ...mapState(['categoryOptions']),
        // Subcategories can be present in multiple supercategories, but
        // select2 doesn't handle that properly. So make subcategory ids unique by
        // postfixing a counter.
        // TODO: Copy/paste from cv EditJobWishes
        uniqueCategoryOptions() {
            const seen = {};
            return this.categoryOptions
                .map(([superName, subCats]) => {
                    return [
                        superName,
                        subCats.map(([subName, subId]) => {
                            const count = (seen[subId] || 0) + 1;
                            seen[subId] = count;
                            return [
                                subName,
                                count > 1 ? `${subId}:${count}` : subId
                            ];
                        })
                    ];
                });
        },
        usesRadius() {
            return this.formData.search_params.address && this.formData.search_params.radius;
        },
        traveltimeExplanation2() {
            return {
                translation: T.traveltimeExplanation2(),
                render: {
                    link: (h, text) => h('a', { href: this.$store.state.urls.editProfile, target: '_blank', rel: 'noopener' }, text),
                },
            };
        },
        oldCategoryNames() {
            const jobagent = this.jobagent;
            if (jobagent !== null) {
                return jobagent.data_names.old_categories.map(x => x.name);
            } else {
                return [];
            }
        },
    },
    methods: {
        save() {
            this.wasValidated = true;
            if (this.validateForm({ report: true }) !== true) {
                return Promise.resolve();
            }

            this.processing = true;

            const payload = {
                id: this.jobagent ? this.jobagent.id : null,
                ...cloneDeep(this.formData),
            };
            if (payload.search_params.subid) {
                payload.search_params.subid = payload.search_params.subid.filter(x => !!x).map(s => typeof s === 'number' ? s : s.replace(/:.*/, ''));
            }
            if (this.source) {
                payload.source = this.source;
            }
            if (!this.usesRadius) {
                payload.search_params.radius = 0;
                payload.search_params.address = '';
            }

            const action = this.isNew ? 'createJobagent' : 'saveJobagent';
            return this.$store.dispatch(action, payload)
                .then(() => this.$emit('done', { email: payload.email }))
                .catch((error) => {
                    if (error instanceof ApiConflictError && error.error === 'duplicate_jobagent') {
                        this.duplicateJobagent = error.extraData.duplicate_jobagent;
                    } else if (error instanceof ApiValidationError) {
                        for (const error of error.errors) {
                            if (error.field) {
                                this.validationErrors[error.field] = error.error_message;
                            }
                        }
                    } else {
                        // TODO: handle validation error
                        throw error;
                    }
                })
                .finally(() => this.processing = false);
        },
        isDirty() {
            return !isEqual(this.formData, this.origFormData);
        },
        clearValidation() {
            this.validationErrors = {};
            this.wasValidated = false;
        },
        validateForm(args = {}) { // TODO: Copy/paste from html/src/lib/components/cv/edit/sections/ValidationMixin.vue
            if (args.update && !this.wasValidated) {
                return;
            }
            const elems = Array.from(this.$el.elements).filter(x => x.hasAttribute('name'));
            let ok = true;
            for (let elem of elems) {
                ok = ok && (args.report ? elem.reportValidity() : elem.checkValidity());
            }
            if (this.customValidation) {
                ok = ok && this.customValidation(args);
            }
            return ok;
        },
    },
    watch: {
        'formData.email': function() {
            // FIXME: This allows us to retry a submission, but it will mark the field as valid when typing...
            delete this.validationErrors.email;
        },
        validationErrors: {
            handler(errors) {
                const fieldNames = ['email'];
                for (let name of fieldNames) {
                    const elem = this.$el.querySelector(`[name=${name}]`);
                    if (errors[name]) {
                        elem.setCustomValidity(errors[name]);
                    } else {
                        elem.setCustomValidity("");
                    }
                }
                if (this.wasValidated) {
                    this.validateForm();
                }
            },
            deep: true,
        },
    },
};
</script>

<template>
    <form
        :class="['jobagent-form', { 'was-validated': wasValidated }]"
        @submit.prevent="save"
    >
        <!-- TODO: radius -->
        <input type="hidden" v-model="formData.search_params.address">
        <input type="hidden" v-model="formData.search_params.radius">
        <div class="jobagent-form__card mb-4">
            <div class="row">
                <div class="col-12">
                    <div class="jobagent-form__main">
                        <DsAlert
                            type="warning"
                            @closed="duplicateJobagent = null"
                            v-if="duplicateJobagent"
                            :key="+new Date()"
                            dismissable
                        >
                            {{ T.duplicateWarning() }}
                            <a href="#" @click.prevent="$emit('view-jobagent', duplicateJobagent)">{{ duplicateJobagent.display_name }}</a>
                        </DsAlert>

                        <DsAlert type="info" v-if="explain && explain === 'traveltime'">
                            <h5>{{ T.traveltimeExplanationHeadline() }}</h5>
                            <p>{{ T.traveltimeExplanationIntro() }}</p>
                            <ul>
                                <li>{{ T.traveltimeExplanation1() }}</li>
                                <RenderTranslation tag="li" :message="traveltimeExplanation2" />
                            </ul>
                            <RenderTranslation tag="p" :message="T.traveltimeExplanationTip()" />
                        </DsAlert>

                        <div class="form-group required" v-if="!isLoggedIn">
                            <label class="control-label" for="jobagent-email">{{ T.email() }}</label>
                            <input
                                type="text"
                                id="jobagent-email"
                                v-model="formData.email"
                                class="form-control"
                                name="email"
                                autocomplete="email"
                                required
                            >
                        </div>

                        <div class="form-group">
                            <label class="control-label" for="jobagent-q">{{ T.keywords() }}</label>
                            <input
                                type="text"
                                id="jobagent-q"
                                v-model="formData.search_params.q"
                                class="form-control"
                                name="q"
                            >
                        </div>

                        <div class="form-group">
                            <label class="control-label" for="jobagent-subid">{{ T.categories() }}</label>
                            <Select2
                                id="jobagent-subid"
                                name="subid"
                                v-model="formData.search_params.subid"
                                class="form-control"
                                multiple
                            >
                                <optgroup v-for="([superName, subCats]) in uniqueCategoryOptions" :key="superName" :label="superName">
                                    <option
                                        v-for="([name, id]) in subCats"
                                        :key="id"
                                        :value="id"
                                    >
                                        {{ name }}
                                    </option>
                                </optgroup>
                            </Select2>
                        </div>

                        <div class="form-group" v-if="oldCategoryNames.length > 0">
                            <label class="control-label text-warning" for="jobagent-old-subid">
                                {{ T.oldCategories() }}
                                <SvgIcon
                                    v-bs-tooltip="T.oldCategoryTooltip()"
                                    name="tooltip"
                                    size="inline"
                                />
                            </label>
                            <SentenceList class="d-block" id="jobagent-old-subid" :items="oldCategoryNames" />
                        </div>

                        <div class="form-group">
                            <label class="control-label" for="jobagent-geoareaid">{{ T.geoDistance() }}</label>
                            <DsGeoRadiusField
                                v-model:radius="formData.search_params.radius"
                                v-model:address="formData.search_params.address"
                                radius-name="radius"
                                address-name="address"
                                radius-field-id="jobagent-geoareaid"
                                :cols-slider="4"
                                :cols-input="8"
                            />

                            <RadiusMap
                                v-if="usesRadius"
                                :address="formData.search_params.address"
                                :radius="formData.search_params.radius"
                                class="mt-3"
                                style="height: 200px"
                            />

                            <button
                                type="button"
                                class="btn btn-sm btn-secondary"
                                :class="{ 'mt-3': usesRadius }"
                                v-if="!showAreaField"
                                @click="showAreaField = true"
                            >
                                {{ T.addAreas() }}
                            </button>
                        </div>

                        <div class="form-group" v-show="showAreaField">
                            <label class="control-label" for="jobagent-geoareaid">{{ T.areas() }}</label>
                            <Select2
                                id="jobagent-geoareaid"
                                v-model="formData.search_params.geoareaid"
                                class="form-control"
                                :config="geoareaConfig"
                            />
                        </div>

                        <div class="form-group">
                            <label class="control-label" for="jobagent-companyid">{{ T.companies() }}</label>
                            <Select2
                                id="jobagent-companyid"
                                name="companyid"
                                v-model="formData.search_params.companyid"
                                class="form-control"
                                :config="companyConfig"
                                multiple
                            >
                                <option
                                    v-for="item in (jobagent ? jobagent.data_names.companies : [])"
                                    :key="item.companyid"
                                    :value="item.companyid"
                                >
                                    {{ item.name }}
                                </option>
                            </Select2>
                            <small class="form-text text-muted">
                                {{ T.companiesHelpText() }}
                            </small>
                        </div>

                        <div class="form-group mb-0">
                            <label class="control-label" for="jobagent-name">{{ T.name() }}</label>
                            <input
                                type="text"
                                id="jobagent-name"
                                v-model="formData.name"
                                class="form-control"
                            >
                            <small class="form-text text-muted">
                                {{ T.nameHelpText() }}
                            </small>
                        </div>
                    </div>
                </div>
                <div class="col-12">
                    <div class="jobagent-form__options">
                        <div class="form-group">
                            <DsCheckbox v-model="formData.enabled" id="jobagent-enabled">
                                {{ T.enableLabel() }}
                            </DsCheckbox>
                            <small class="form-text text-muted" v-if="formData.enabled && isLoggedIn">
                                {{ T.emailInstruction($store.state.userEmail, T.settings()) }}
                            </small>
                        </div>

                        <hr>

                        <DsRadioButton
                            v-model="formData.frequency"
                            id="jobagent-frequency-daily"
                            name="frequency"
                            value="daily"
                        >
                            {{ T.daily() }}
                        </DsRadioButton>
                        <DsRadioButton
                            v-model="formData.frequency"
                            id="jobagent-frequency-weekly"
                            name="frequency"
                            value="weekly"
                        >
                            {{ T.weekly() }}
                        </DsRadioButton>

                        <hr>

                        <div class="form-group mb-0">
                            <label class="control-label" for="jobagent-transportation">{{ T.preferredTransportation() }}</label>
                            <select
                                class="form-control"
                                name="preferred_transportation"
                                id="jobagent-transportation"
                                v-model="formData.preferred_transportation"
                            >
                                <option value="automatic">
                                    {{ T.automatic() }}
                                </option>
                                <option value="bike">
                                    {{ T.bike() }}
                                </option>
                                <option value="car">
                                    {{ T.car() }}
                                </option>
                                <option value="transit">
                                    {{ T.transit() }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="btn-md-list">
            <button
                type="button"
                class="btn btn-secondary"
                @click="$emit('done')"
                v-if="isLoggedIn"
            >
                {{ T.cancel() }}
            </button>
            <PromiseButton
                type="submit"
                class="btn btn-primary"
                ref="save"
            >
                {{ T.save() }}
                <template #inprogress>
                    {{ T.saving() }}
                </template>
            </PromiseButton>
        </div>

        <BlockInteraction :request-in-progress="processing" />
    </form>
</template>
